<template>
  <div>
    <import-dialog
      title="Import FX Rates"
      :message="importMessage"
      :validate="true"
      uploadUrl="/api/list/FxRate/importFxRates"
      :visible="showImportDialog"
      :marketId="marketId"
      v-if="showImportDialog"
      @close="toggleImportDialog"
      @onSuccess="onSuccess"
    ></import-dialog>
    <page-toolbar style="height:61px;padding-top:5px">
      <vonap-toolbar-label label="">
        <kendo-button
          icon="check"
          title="Save changes"
          @click="saveChanges"
          v-if="!isReadOnly"
        ></kendo-button>
        <kendo-button
          icon="cancel"
          title="Cancel changes"
          @click="cancelChanges"
          v-if="!isReadOnly"
        ></kendo-button>
      </vonap-toolbar-label>
      <vonap-toolbar-label label="Forecast Date">
        <kendo-dropdownlist
          v-model="forecastDate"
          :dataSource="fxRateDates"
          data-text-field="forecastDateText"
          data-value-field="forecastDate"
          :filter="'contains'"
          style="width:114px"
          @change="onForecastDateChange"
        ></kendo-dropdownlist>
      </vonap-toolbar-label>
      <vonap-toolbar-label label="From">
        <kendo-dropdownlist
          v-model="fromDate"
          :dataSource="fromDates"
          data-text-field="dateText"
          data-value-field="dateValue"
          :autoBind="false"
          :filter="'contains'"
          style="width:104px"
          @change="onFromDateChange"
        ></kendo-dropdownlist>
      </vonap-toolbar-label>
      <vonap-toolbar-label label="To">
        <kendo-dropdownlist
          v-model="toDate"
          :dataSource="toDates"
          data-text-field="dateText"
          data-value-field="dateValue"
          :autoBind="false"
          :filter="'contains'"
          style="width:104px"
          @change="onToDateChange"
        ></kendo-dropdownlist>
      </vonap-toolbar-label>
      <vonap-toolbar-label label="">
        <kendo-button @click="addDate" title="Add date" icon="calendar-date" v-if="!isReadOnly"
          >Add</kendo-button
        >
        <kendo-button
          @click="deleteDate"
          title="Delete date"
          icon="calendar"
          v-if="!isReadOnly && forecastDate != null && !/0001/.test(forecastDate)"
          >Delete</kendo-button
        >
        <kendo-button icon="add" title="Add row" @click="addRow" v-if="!isReadOnly"> </kendo-button>
      </vonap-toolbar-label>
      <visionap-switch-list
        :data-source="forecastRateCcy"
        :value="forecastRateCcyDataSource"
        data-value-field="ccyPairId"
        data-text-field="ccyPairCode"
        label-text="Ccy/Comm Pair"
        item-icon="monetization_on"
        :number-of-items="1"
        :show-select-all="true"
        select-all-label="Select all"
        width="232px"
        style="margin:-8px 10px"
        @input="forecastRateCcy = $event"
      ></visionap-switch-list>
      <vonap-toolbar-label label="">
        <kendo-button
          type="button"
          title="Import data"
          @click="toggleImportDialog"
          v-if="!isReadOnly"
        >
          <v-icon small>archive</v-icon>
        </kendo-button>
        <kendo-button type="button" title="Open Chart" @click="showChart">
          <v-icon small>show_chart</v-icon>
        </kendo-button>
        <kendo-button type="button" title="Export to Excel" @click="exportToExcel">
          <i class="k-icon k-i-excel"></i>
        </kendo-button>
      </vonap-toolbar-label>
    </page-toolbar>
    <kendo-grid
      height="calc(100vh - 64px - 32px - 75px)"
      ref="fxRateGrid"
      :columns="columns"
      :dataSource="dataSource"
      :editable="!isReadOnly"
      :navigatable="true"
      @databound="gridDataBound"
    ></kendo-grid>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {numericEditor} from '../../helpers/kendo-editors'
import axios from 'axios'
import ImportDialog from '../../components/ImportDialog'
import confirm from '../../helpers/confirmation-window'
import notification from '../../helpers/notification'
import PageToolbar from '@/components/PageToolbar'
import forecastDateWindow from '@/components/List/DivisionForecast/forecastDateWindow'
import security from '@/mixins/security'
import {getDataItem} from '@/helpers/kendo-helpers'
import VisionapSwitchList from '@/components/VisionapSwitchList'
import ratesChart from '../../dashboard-components/rates-chart'
import VonapToolbarLabel from '@/components/VonapToolbarLabel'

export default {
  name: 'FxRates',
  mixins: [security],
  computed: {
    ...mapGetters({marketId: 'app/marketId'}),
    dataSource() {
      if (!this.data) return
      return new kendo.data.DataSource({
        transport: {
          read: options => {
            options.success(this.data)
          },
          update: this.update,
        },
        schema: {
          model: {
            id: 'Period',
            fields: this.getDynamicFields(),
          },
        },
        batch: true,
      })
    },
    columns() {
      const ccyColumns = this.ccyColumns.map(column => ({
        ...column,
        width: 100,
        attributes: {style: 'text-align: right;'},
        editor: numericEditor({
          format: 'n5',
          spinners: false,
          decimals: 16,
          step: 0,
        }),
      }))

      let allColumns = [
        {field: 'PeriodMonth', title: 'Month', visible: true, width: 90},
        ...ccyColumns,
        {
          width: '60px',
          attributes: {style: 'text-align: center;'},
          template: dataItem => {
            if (!dataItem.CanBeDeleted || this.isReadOnly) {
              return ''
            }
            const template = `<a class="deleteRate" title="Delete" data-Period="${
              dataItem.Period
            }" href="javascript:;"> <span class="k-icon k-i-delete"></span></a>`
            return template
          },
        },
        {},
      ]
      return allColumns
    },
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({tokenString: 'authentication/tokenString'}),
  },
  methods: {
    gridDataBound() {
      kendo.jQuery(this.$refs.fxRateGrid.$el).off('click', '.deleteRate')
      kendo.jQuery(this.$refs.fxRateGrid.$el).on('click', '.deleteRate', e => {
        const dataItem = getDataItem(e.target)
        const period = dataItem.Period
        this.delete(period)
      })
    },
    async update(e) {
      const data = e.data.models.map(this.getDataItem)

      await axios
        .put('/api/list/FxRate', {
          dataItems: data,
          clientId: this.clientId,
          marketId: this.marketId,
        })
        .catch(({response}) => {
          e.error('Error', response.status, response.statusText)
        })

      e.success(true)
    },
    async delete(period) {
      await axios
        .delete('/api/list/FxRate', {
          params: {
            period: period,
            clientId: this.clientId,
            marketId: this.marketId,
            forecastDate: this.forecastDate,
          },
        })
        .then(() => {})
        .catch(() => {})
      this.reloadData()
    },
    getDynamicFields() {
      const dynamicFields = this.ccyColumns.reduce((acc, {field}) => {
        acc[field] = {
          field,
          editable: !this.isReadOnly,
          required: true,
          type: 'number',
        }
        return acc
      }, {})

      return {
        Period: {type: 'number', defaultValue: 0, editable: false},
        ForecastDate: {type: 'date', editable: false},
        PeriodMonth: {editable: false, type: 'string'},

        ...dynamicFields,

        CanBeDeleted: {type: 'boolean', defaultValue: true, editable: false},
      }
    },
    getDataItem(data) {
      const period = data.Period
      const forecastDate = kendo.toString(data.ForecastDate, 'yyyy-MM-dd')
      const fxCcyRateItems = this.ccyColumns.map(column => ({
        value: data[column.field],
        field: `${column.ccyPairId}`,
      }))

      return {
        period,
        forecastDate,
        fxCcyRateItems,
      }
    },
    addDate() {
      let vm = this
      const invalidDates = vm.fxRateDates
        .data()
        .filter(function(element) {
          return element.dateText !== 'New Date'
        })
        .map(function(element) {
          return kendo.toString(element.date, 'yyyy-MM-dd')
        })

      var suggestedPeriodsTotal = vm.dataSource.data().length
      forecastDateWindow.openFxRateForecast(vm.clientId, invalidDates, suggestedPeriodsTotal)
      forecastDateWindow.onSaveChanges(function() {
        vm.fxRateDates.read()
        this.reloadData()
      })
    },
    deleteDate() {
      confirm.openConfirmationWindow(
        'Delete?',
        'Are you sure you want to delete the selected date?',
        () => {
          axios
            .delete('/api/list/FxRate/deleteDate', {
              params: {
                clientId: this.clientId,
                marketId: this.marketId,
                forecastDate: this.forecastDate,
              },
            })
            .then(() => {
              return this.fxRateDates.read()
            })
            .catch(() => notification('Error deleting date', 'error'))
        },
      )
    },
    async addRow() {
      const {clientId, forecastDate, marketId} = this
      const {data} = await axios
        .post('/api/list/FxRate', {clientId, marketId, forecastDate})
        .catch(() => notification('Error getting rates', 'error'))
      const {period: Period, periodMonth: PeriodMonth} = data

      if (Period && PeriodMonth) {
        const row = {Period, PeriodMonth, ForecastDate: forecastDate}
        this.ccyColumns.reduce((acc, {field}) => {
          acc[field] = 0
          return acc
        }, row)

        this.reloadData()
        //this.data = [...this.data, row]
      }
    },
    saveChanges() {
      const grid = this.$refs.fxRateGrid.kendoWidget()
      grid.saveChanges()
    },
    cancelChanges() {
      const grid = this.$refs.fxRateGrid.kendoWidget()
      grid.cancelChanges()
    },
    onSuccess(e) {
      if (e.status == 0) {
        notification(e.message, 'info')
        this.fxRateDates.read()
        this.reloadData()
      } else {
        this.importMessage = e.message
        notification(e.message, 'error')
      }
    },
    toggleImportDialog() {
      this.importMessage = ''
      this.showImportDialog = !this.showImportDialog
    },
    exportToExcel() {
      const ccyPairs = JSON.stringify(this.forecastRateCcy).toString()
      const url = `/api/list/FxRate/Export?clientId=${this.clientId}
      &marketId=${this.marketId}
      &forecastDate=${this.forecastDate}
      &ccyPairs=${ccyPairs}
      &fromDate=${this.fromDate}
      &toDate=${this.toDate}
      &t=${this.tokenString}`
      location.href = url
    },
    onForecastDateChange({sender}) {
      this.forecastDate = sender.value()
      this.fromDates.read()
    },
    onFromDateChange({sender}) {
      this.fromDate = sender.value()
      this.toDates.read()
    },
    onToDateChange({sender}) {
      this.toDate = sender.value()
      this.reloadData()
    },
    async getForecastRateCcy() {
      const {data: forecastRateCcyData} = await axios.get(
        `/api/list/FxRate/getForecastRateCcy?clientId=${this.clientId}&marketId=${this.marketId}`,
        {},
      )
      this.forecastRateCcyDataSource = forecastRateCcyData
      const selectedValues = forecastRateCcyData.map(x => x.ccyPairId)
      this.forecastRateCcy = selectedValues
    },
    async reloadData() {
      const clientId = this.clientId
      const marketId = this.marketId
      const forecastDate = this.forecastDate
      const ccyPairs = JSON.stringify(this.forecastRateCcy).toString()
      const fromDate = this.fromDate
      const toDate = this.toDate
      const response = await axios.get('/api/list/FxRate', {
        params: {clientId, marketId, forecastDate, ccyPairs, fromDate, toDate},
      })
      const {columns, data} = response.data

      this.ccyColumns = columns
      this.data = data
    },
    showChart() {
      const forecastDate = kendo.toString(this.forecastDate, 'yyyy-MM-dd')
      let options = {
        forecastDate: forecastDate,
        clientId: this.clientId,
        marketId: this.marketId,
        ccyPairs: this.forecastRateCcy,
        fromDate: this.fromDate,
        toDate: this.toDate,
      }

      ratesChart.openChart(options)
    },
  },
  watch: {
    async forecastRateCcy(forecastRateCcy) {
      if (!forecastRateCcy) return

      this.reloadData()
    },
  },
  data() {
    return {
      forecastDate: null,
      ccyColumns: [],
      data: [],
      fxRateDates: new kendo.data.DataSource({
        transport: {
          read: {
            url: () => `/api/list/FxRate/getFxRateForecastDates?clientId=${this.clientId}`,
            type: 'GET',
          },
        },
        requestEnd: ({response}) => {
          if (!response) return
          const [first] = response
          this.forecastDate = first.forecastDate
          this.fromDates.read()
        },
      }),
      fromDate: null,
      fromDates: new kendo.data.DataSource({
        transport: {
          read: {
            url: () =>
              `/api/list/FxRate/getFromDates?clientId=${this.clientId}&marketId=${
                this.marketId
              }&forecastDate=${this.forecastDate}`,
            type: 'GET',
          },
        },
        requestEnd: ({response}) => {
          if (!response) return
          const [first] = response
          this.fromDate = first.dateValue
          this.toDates.read()
        },
      }),
      toDate: null,
      toDates: new kendo.data.DataSource({
        transport: {
          read: {
            url: () =>
              `/api/list/FxRate/getToDates?clientId=${this.clientId}&marketId=${
                this.marketId
              }&forecastDate=${this.forecastDate}&fromDate=${this.fromDate}`,
            type: 'GET',
          },
        },
        requestEnd: ({response}) => {
          if (!response) return
          const [first] = response
          this.toDate = first.dateValue
          this.getForecastRateCcy()
        },
      }),
      showImportDialog: false,
      importMessage: '',
      forecastRateCcy: [-1],
      forecastRateCcyDataSource: [],
    }
  },
  components: {
    PageToolbar,
    ImportDialog,
    VisionapSwitchList,
    VonapToolbarLabel,
  },
}
</script>
